import AppShell from "@/components/AppShell";
import { Link } from "react-router-dom";
import {
  ShieldCheck, Lock, Database, Sparkles, Eye, Trash2,
  Server, KeyRound, Bell, FileWarning,
} from "lucide-react";

/* Public "Security & Privacy" trust page — linked from the disclaimer
 * footer. Written for petitioners and attorneys evaluating whether to
 * trust us with sensitive immigration data.
 */

export default function SecurityPrivacyPage() {
  return (
    <AppShell showNav={false}>
      <div className="mx-auto max-w-4xl px-6 py-14" data-testid="security-privacy-page">
        <Link
          to="/"
          className="text-xs font-semibold mb-8 inline-block"
          style={{ color: "var(--action)" }}
        >
          &larr; Back to home
        </Link>

        {/* Hero */}
        <div className="flex items-start gap-4 mb-10">
          <div
            className="p-3 rounded-xl flex-shrink-0"
            style={{ background: "var(--ai-bg)" }}
          >
            <ShieldCheck className="w-8 h-8" style={{ color: "var(--ai-text)" }} />
          </div>
          <div>
            <h1
              className="font-display text-4xl font-extrabold leading-tight"
              style={{ color: "var(--text-primary)" }}
            >
              How we protect your data
            </h1>
            <p className="mt-2 text-base" style={{ color: "var(--text-secondary)" }}>
              A plain-English summary of the security and privacy controls that
              guard your immigration paperwork on DS-260USA.com. For the
              formal versions see our{" "}
              <Link to="/legal/privacy" style={{ color: "var(--action)" }}>
                Privacy Policy
              </Link>{" "}
              and{" "}
              <Link to="/legal/terms" style={{ color: "var(--action)" }}>
                Terms of Service
              </Link>.
            </p>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <TrustCard
            testId="trust-passwords"
            icon={<Lock className="w-5 h-5" />}
            title="Your password is never stored"
          >
            Passwords are hashed with <strong>bcrypt</strong> before they touch
            our database. Even our own team &mdash; and anyone who ever manages
            to read our database &mdash; cannot recover them.
          </TrustCard>

          <TrustCard
            testId="trust-cookies"
            icon={<KeyRound className="w-5 h-5" />}
            title="Sessions live in HttpOnly cookies"
          >
            Your login token is stored in an <strong>HttpOnly, Secure,
            SameSite=Lax</strong> cookie. It cannot be read by JavaScript and
            it does not travel to other websites &mdash; two of the biggest
            defenses against session theft.
          </TrustCard>

          <TrustCard
            testId="trust-invite"
            icon={<ShieldCheck className="w-5 h-5" />}
            title="Private beta by default"
          >
            The site is invitation-only. Registration requires a code you
            received from us, so opportunistic visitors can&rsquo;t create
            accounts to poke around.
          </TrustCard>

          <TrustCard
            testId="trust-isolation"
            icon={<Database className="w-5 h-5" />}
            title="Every request checks case ownership"
          >
            Every API endpoint that touches a case verifies you own it. There
            is no code path in which one user can read, download, or modify
            another user&rsquo;s petition &mdash; and this is protected by an
            automated test suite that runs on every deploy.
          </TrustCard>

          <TrustCard
            testId="trust-tls"
            icon={<Server className="w-5 h-5" />}
            title="HTTPS everywhere"
          >
            All traffic is encrypted in transit with TLS. The connection between
            your browser and our server cannot be read or altered by anyone
            in between &mdash; not your coffee shop&rsquo;s Wi-Fi, not your ISP.
          </TrustCard>

          <TrustCard
            testId="trust-ai"
            icon={<Sparkles className="w-5 h-5" />}
            title="AI processing — what actually happens"
          >
            When you upload a document, we send the page images to Anthropic&rsquo;s
            Claude Sonnet 4.5 model to read them, translate them, and fill your
            form fields. That data is used strictly to serve your request. We
            do <strong>not</strong> sell your data. We do <strong>not</strong>{" "}
            train models on it.
          </TrustCard>

          <TrustCard
            testId="trust-ttl"
            icon={<Trash2 className="w-5 h-5" />}
            title="Smart Sort staging auto-deletes"
          >
            When you drop a stack of documents through Smart Sort, the raw
            page images sit in a temporary staging area only long enough for
            you to review and apply them. If you don&rsquo;t confirm within an
            hour, the staged images are automatically deleted.
          </TrustCard>

          <TrustCard
            testId="trust-analytics"
            icon={<Eye className="w-5 h-5" />}
            title="No third-party trackers"
          >
            No Google Analytics. No Facebook pixel. No ad networks. No hidden
            third parties reading your immigration data. Just you, our servers,
            and the OCR provider needed to fill your forms.
          </TrustCard>

          <TrustCard
            testId="trust-audit"
            icon={<FileWarning className="w-5 h-5" />}
            title="Every change is logged"
          >
            Every edit to a case field is recorded to a per-case audit log with
            timestamp, source (typed, OCR, or corrected), and the person who
            made the change. If a form goes out with a wrong value, you can
            trace where it came from.
          </TrustCard>

          <TrustCard
            testId="trust-deletion"
            icon={<Trash2 className="w-5 h-5" />}
            title="You can delete your data"
          >
            Ask us to remove your account and all its data at any time by
            emailing{" "}
            <a href="mailto:privacy@ds-260usa.com" style={{ color: "var(--action)" }}>
              privacy@ds-260usa.com
            </a>. Depending on your jurisdiction (California, EU, others) you
            may have additional formal rights spelled out in our{" "}
            <Link to="/legal/privacy" style={{ color: "var(--action)" }}>
              Privacy Policy
            </Link>.
          </TrustCard>

          <TrustCard
            testId="trust-headers"
            icon={<ShieldCheck className="w-5 h-5" />}
            title="Hardened HTTP responses"
          >
            Our server sends security headers on every response &mdash;
            <code className="mx-1 text-[11px]">X-Frame-Options</code>,
            <code className="mx-1 text-[11px]">X-Content-Type-Options</code>,
            <code className="mx-1 text-[11px]">Referrer-Policy</code> &mdash;
            to protect against clickjacking, MIME sniffing, and referer leaks.
          </TrustCard>

          <TrustCard
            testId="trust-audits"
            icon={<Bell className="w-5 h-5" />}
            title="Externally audited"
          >
            Our codebase is reviewed by an independent security audit tool
            before every deploy. Findings are triaged and remediated with
            automated regression tests that lock in the fixes.
          </TrustCard>
        </div>

        {/* What we ask of YOU */}
        <div className="mt-12 card-surface p-6" data-testid="trust-user-responsibilities">
          <h2
            className="font-display text-lg font-bold mb-3"
            style={{ color: "var(--text-primary)" }}
          >
            What we ask of you
          </h2>
          <ul className="space-y-2 text-sm" style={{ color: "var(--text-primary)" }}>
            <li>
              <strong>Use a unique password</strong> for this account &mdash; not one
              you&rsquo;ve used elsewhere. A password manager makes this painless.
            </li>
            <li>
              <strong>Do not share your invite code</strong> with anyone you
              don&rsquo;t personally trust. If you suspect a code has leaked,
              email us and we&rsquo;ll rotate it.
            </li>
            <li>
              <strong>Log out on shared computers</strong>, especially at libraries
              or attorneys&rsquo; offices.
            </li>
            <li>
              <strong>Review every form before you file it.</strong> The AI is
              accurate but not infallible &mdash; treat every generated PDF as a
              first draft, not the final word.
            </li>
          </ul>
        </div>

        {/* Report a concern */}
        <div
          className="mt-6 p-6 rounded-lg text-sm"
          data-testid="trust-report"
          style={{ background: "var(--ai-bg)", border: "1px solid var(--ai-border)" }}
        >
          <h2
            className="font-display font-bold mb-1"
            style={{ color: "var(--text-primary)" }}
          >
            Report a security concern
          </h2>
          <p style={{ color: "var(--text-secondary)" }}>
            Found a vulnerability or noticed something unusual? Please email{" "}
            <a href="mailto:security@ds-260usa.com" style={{ color: "var(--action)", fontWeight: 600 }}>
              security@ds-260usa.com
            </a>{" "}
            &mdash; we investigate every report and respond within 48 hours.
            We do not pursue legal action against good-faith researchers who
            follow responsible disclosure.
          </p>
        </div>

        <p
          className="mt-8 text-xs italic text-center"
          style={{ color: "var(--text-secondary)" }}
        >
          Last reviewed: February 14, 2026 · This page is a plain-English
          summary. See our{" "}
          <Link to="/legal/privacy" style={{ color: "var(--action)" }}>Privacy Policy</Link>{" "}
          and{" "}
          <Link to="/legal/terms" style={{ color: "var(--action)" }}>Terms of Service</Link>{" "}
          for the formal versions.
        </p>
      </div>
    </AppShell>
  );
}

function TrustCard({ icon, title, children, testId }) {
  return (
    <div
      className="card-surface p-5"
      data-testid={testId}
    >
      <div className="flex items-start gap-3 mb-2">
        <div
          className="p-2 rounded-lg flex-shrink-0"
          style={{ background: "var(--ai-bg)", color: "var(--ai-text)" }}
        >
          {icon}
        </div>
        <h3
          className="font-display font-bold text-sm leading-tight"
          style={{ color: "var(--text-primary)" }}
        >
          {title}
        </h3>
      </div>
      <p
        className="text-xs leading-relaxed pl-11"
        style={{ color: "var(--text-secondary)" }}
      >
        {children}
      </p>
    </div>
  );
}
